import { useEffect, useCallback } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface UseRealtimeQuotesProps {
  bookingId: string;
  enabled?: boolean;
}

export const useRealtimeQuotes = ({ bookingId, enabled = true }: UseRealtimeQuotesProps) => {
  const queryClient = useQueryClient();

  const handleQuoteUpdate = useCallback((payload: any) => {
    console.log('[REALTIME-QUOTES] Received quote update:', payload);
    
    const updatedQuote = payload.new;
    const quoteBookingId = updatedQuote.booking_id;
    
    // Only invalidate if it's for the current booking
    if (quoteBookingId === bookingId) {
      console.log('[REALTIME-QUOTES] Invalidating quotes cache for booking:', bookingId);
      queryClient.invalidateQueries({ queryKey: ['quotes', bookingId] });
    }
  }, [queryClient, bookingId]);

  const handleQuoteInsert = useCallback((payload: any) => {
    console.log('[REALTIME-QUOTES] Received quote insert:', payload);
    
    const newQuote = payload.new;
    const quoteBookingId = newQuote.booking_id;
    
    if (quoteBookingId === bookingId) {
      console.log('[REALTIME-QUOTES] Invalidating quotes cache for new quote:', bookingId);
      queryClient.invalidateQueries({ queryKey: ['quotes', bookingId] });
    }
  }, [queryClient, bookingId]);

  const handleQuoteDelete = useCallback((payload: any) => {
    console.log('[REALTIME-QUOTES] Received quote delete:', payload);
    
    const deletedQuote = payload.old;
    const quoteBookingId = deletedQuote.booking_id;
    
    if (quoteBookingId === bookingId) {
      console.log('[REALTIME-QUOTES] Invalidating quotes cache after deletion:', bookingId);
      queryClient.invalidateQueries({ queryKey: ['quotes', bookingId] });
    }
  }, [queryClient, bookingId]);

  useEffect(() => {
    if (!enabled || !bookingId) return;

    console.log('[REALTIME-QUOTES] Setting up realtime subscription for booking:', bookingId);
    
    let channel: RealtimeChannel;
    
    const setupRealtimeSubscription = async () => {
      channel = supabase
        .channel(`quotes-${bookingId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'quotes',
            filter: `booking_id=eq.${bookingId}`
          },
          handleQuoteUpdate
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'quotes',
            filter: `booking_id=eq.${bookingId}`
          },
          handleQuoteInsert
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'quotes',
            filter: `booking_id=eq.${bookingId}`
          },
          handleQuoteDelete
        )
        .subscribe((status) => {
          console.log('[REALTIME-QUOTES] Subscription status:', status);
        });
    };

    setupRealtimeSubscription();

    return () => {
      console.log('[REALTIME-QUOTES] Cleaning up realtime subscription for booking:', bookingId);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [bookingId, enabled, handleQuoteUpdate, handleQuoteInsert, handleQuoteDelete]);

  return {
    // Expose methods if needed
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', bookingId] });
    }
  };
};